/*
 * App Router version of `../invoiceOrderHandler/createInvoiceOrderHandler`.
 */
import { NextResponse } from 'next/server'
import {
  type CreateInvoiceOrderClientRequest,
  type CreateInvoiceOrderDTO,
  type CreateOrderItemDTO,
  type DiscountDTO,
  type Option,
  OrderStatus,
  type ShipmentDTO,
  type ShippingProviderName
} from 'ecosystem'
import { computeOrderTotalsFromGross, parseStoreToBackendOrderLines } from 'api'
import storefrontApi from 'api/storefront-api'
import { buildShipmentFromDefault } from '../shippingProviders'
import { openCarrierSession } from '../shippingProviders/carrierSession'
import { withInvoiceGuards } from './withInvoiceGuards'
import { type InvoiceOrderHandlerOpts } from './types'

interface BuildInvoiceDraftArgs {
  opts: InvoiceOrderHandlerOpts
  storeId: string
  items: CreateOrderItemDTO[]
  cartDiscountCode: Option<DiscountDTO>
  shipping?: {
    provider: ShippingProviderName
    providerSessionId?: string
  }
}

const buildAndCreateDraft = async ({
  opts,
  storeId,
  items,
  cartDiscountCode,
  shipping
}: BuildInvoiceDraftArgs) => {
  // No shipping args: nothing has been quoted yet at DRAFT time, so these are
  // the items-only totals. `submitInvoiceOrderRouteHandler` recomputes them
  // with the resolved `shippingCost` before flipping to CREATED — that
  // recompute is what makes `orderAmount` the freight-inclusive total the BE
  // publishes.
  const { orderAmount, orderTaxAmount, orderDiscount } = computeOrderTotalsFromGross(items)

  const shipments: ShipmentDTO[] | undefined = shipping
    ? [
        buildShipmentFromDefault({
          provider: shipping.provider,
          shipmentId: shipping.providerSessionId
        }).shipment
      ]
    : undefined

  const payload: CreateInvoiceOrderDTO = {
    storeId,
    customerId: opts.customerId,
    status: OrderStatus.DRAFT,
    currency: opts.currencyCode,
    items,
    orderAmount,
    orderTaxAmount,
    ...(orderDiscount > 0 ? { orderDiscount } : {}),
    ...(shipments ? { shipments } : {}),
    ...(cartDiscountCode?.code ? { discountCode: cartDiscountCode.code } : {})
  }

  return storefrontApi.invoiceOrders.createInvoiceOrder(payload)
}

/**
 * POST /api/orders/invoice — creates a DRAFT invoice order.
 *
 * The carrier half is `openCarrierSession`, shared with the upfront flow:
 * - DEFAULT  → no carrier integration, persist DRAFT with no shipments.
 * - POSTNORD → persist with shipments[0].shippingProvider=POSTNORD; the
 *              carrier widget runs FE-side and pushes booking details via the
 *              update handler.
 * - INGRID   → open an Ingrid checkout session, persist DRAFT with
 *              shipments[0].shipmentId set to the session id.
 * - NSHIFT   → open an Nshift session, same shape.
 *
 * Pricing: items and totals are stored gross (VAT-inclusive). `orderTaxAmount`
 * is the included-VAT portion (single semantics across UPFRONT and INVOICE per
 * FEATURE_B2B_ORDER_PLACEMENT.md §3.5). B2B ex-VAT display is a render-time
 * transformation on the FE, not a storage difference.
 */
export const createInvoiceOrderRouteHandler = (opts: InvoiceOrderHandlerOpts) =>
  withInvoiceGuards(opts, async (request, locale) => {
    const payload = (await request.json()) as CreateInvoiceOrderClientRequest
    const { storeId, items, cartDiscountCode } = payload

    const carrier = await openCarrierSession({ payload, locale }, opts)

    const order = await buildAndCreateDraft({
      opts,
      storeId,
      items: parseStoreToBackendOrderLines(items, cartDiscountCode),
      cartDiscountCode,
      shipping: carrier.shipping
    })

    return NextResponse.json({ storefrontOrderResponse: order, ...carrier.response })
  })
